import React from 'react';
import { connectMediaStore } from '../../_hocs/withOwnReduxStore';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  moveGallection,
  removeGallection,
  RemoveGallectionPayload,
  setGallectionName,
  ChangeGallectionNamePayload,
  MoveGallectionPayload,
} from '../../actions';
import ItemControlButtons from '../ItemControlButtons';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import MoveToFolderIcon from '@wix/da-ds/pkg/Icons/24x24/MoveToFolder';
import CameraIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import SettingsIcon from '@wix/da-ds/pkg/Icons/24x24/Settings';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PromptModal from '@wix/da-ds/pkg/modals/dsPresets/PromptModal';
import GallectionListMenu from '../GallectionListMenu';
import { PapiGallection } from '@wix/da-papi-types';
import {
  getFolderNameValidationErrorMessage,
  MAX_TITLE_LENGTH,
} from '../../../utils/isValidFolderName';
import {
  getCanLabelDeviationsInThisGallection,
  getCanMoveGallection,
  getCanRemoveGallection,
  getCanRenameGallection,
  getCanSetGallectionCover,
  isSpecialStuckGallection,
} from '../../helpers';
import { getSubfoldersByParentId, getFeaturedFolder } from '../../selectors';
import { getCurrentUserHasCore } from '../../../publicSession/selectors';
import { GallectionType } from '../../../types/gallection';
import { isPremiumGallection } from '../../../utils/gallections';
import TextTooltipOnHover from '../../../Popover/Preset/TextTooltipOnHover';
import withGallections, {
  WithGallectionsProps,
} from '../../_hocs/withGallections';
import GallectionCoverSelector from '../GallectionCoverSelector';
import withFeatureFlags, {
  WithFeatureFlagsProps,
} from '../../_hocs/withFeatureFlags';
import LabelAsMenu from '../LabelAsMenu/LabelAsMenu';
import withGruser, { WithGruserProps } from '../../_hocs/withGruser';

export interface Props {
  gallection: PapiGallection;
  existingGallectionNames?: string[];
  className?: string;
  moreMenuClassName?: string;
  hasSubfolders?: boolean;
  featuredFolder?: PapiGallection;
  hasCore?: boolean;
}

type MediaStoreDispatchProps = {
  moveGallection: (payload: MoveGallectionPayload) => any;
  removeGallection: (payload: RemoveGallectionPayload) => any;
  setGallectionName: (payload: ChangeGallectionNamePayload) => any;
};

type State = {
  popupType: 'remove' | 'rename' | 'set-cover' | null;
};

const enhance = compose(
  withGallections,
  withGruser,
  connectMediaStore<{}, MediaStoreDispatchProps, Props, {}>(
    (state, ownProps) => ({
      featuredFolder: getFeaturedFolder(state),
      hasSubfolders:
        getSubfoldersByParentId(state, ownProps.gallection.folderId).length > 0,
      hasCore: getCurrentUserHasCore(state),
    }),
    {
      moveGallection,
      removeGallection,
      setGallectionName,
    }
  ),
  withFeatureFlags,
  withTranslation()
);

export class GallectionControlButtons extends React.Component<
  Props &
    WithTranslation &
    WithGallectionsProps &
    WithGruserProps &
    WithFeatureFlagsProps &
    MediaStoreDispatchProps &
    State
> {
  readonly state: State = {
    popupType: null,
  };
  render() {
    const { className, moreMenuClassName, gallection, gruser, t } = this.props;

    const canRemove = getCanRemoveGallection(gallection);
    const canRename = getCanRenameGallection(gallection);
    const canSetGallectionCover = getCanSetGallectionCover(gallection);
    const canLabel = getCanLabelDeviationsInThisGallection(gruser, gallection);

    return (
      <>
        <ItemControlButtons
          className={className}
          moreMenuClassName={moreMenuClassName}
          menuItems={[
            {
              // safe zone
              items: [
                {
                  value: 1,
                  label: t('mediapopup.itemMenu.rename'),
                  icon: <EditIcon size={IconSize.SMALL} />,
                  disabled: !canRename,
                  onClick: this.openRenamePromptPopup,
                },
                gallection.type !== 'stash' && {
                  value: 1,
                  label: t('mediapopup.itemMenu.changeCover'),
                  icon: <CameraIcon size={IconSize.SMALL} />,
                  disabled: !canSetGallectionCover,
                  onClick: this.openCoverSelectionPopup,
                },
                this.getFolderMoveMenuItem(),
                canLabel && {
                  label: t('mediapopup.itemMenu.labelAllAs'),
                  icon: <SettingsIcon size={IconSize.SMALL} />,
                  submenu: <LabelAsMenu gallection={gallection} />,
                },
              ].filter(item => item),
            },
            {
              // danger zone
              items: [
                {
                  value: 3,
                  label: t('mediapopup.itemMenu.delete'),
                  icon: (
                    <TrashcanIcon size={IconSize.SMALL} disableDefaultColors />
                  ),
                  danger: true,
                  disabled: !canRemove,
                  onClick: this.remove,
                },
              ],
            },
          ]}
        />
        {this.renderPopup()}
      </>
    );
  }

  getFolderMoveMenuItem() {
    const {
      gallection,
      gallections,
      featuredFolder,
      hasCore,
      hasSubfolders,
      t,
      ...props
    } = this.props;
    const isSubfolder = !!gallection.parentId;
    const featuredFolderId = featuredFolder && featuredFolder.folderId;

    if (
      [
        GallectionType.COLLECTION,
        GallectionType.PRIVATE_COLLECTION,
        GallectionType.STASH,
      ].includes(gallection.type as GallectionType)
    ) {
      return null;
    }

    let label: React.ReactNode = isSubfolder
      ? t('mediapopup.itemMenu.moveSubfolder')
      : t('mediapopup.itemMenu.moveFolderTo');

    const isPremium = isPremiumGallection(gallection);

    if (isPremium) {
      const tooltipTransKey =
        gallection.premiumFolderData?.type === 'paid'
          ? 'mediapopup.itemMenu.gallery.moveFolderTo.tooltip.disabledAsPaid'
          : 'mediapopup.itemMenu.gallery.moveFolderTo.tooltip.disabledAsWatchersOnly';
      label = (
        <TextTooltipOnHover
          placement="right"
          tooltipText={t(tooltipTransKey)}
          renderReference={({ ref }) => (
            <span style={{ pointerEvents: 'all' }} ref={ref}>
              {t('mediapopup.itemMenu.moveFolderTo')}
            </span>
          )}
        />
      );
    }

    const isDisabled =
      isSpecialStuckGallection(gallection) ||
      !getCanMoveGallection(gallection) ||
      // we do not allow more than 1 level of nesting
      // so disallow moving a folder that already has subfolders
      // into another folder
      hasSubfolders ||
      // non-core users can't have subfolders, so a
      // folder can't be moved into a sibling (becoming a sub)
      !hasCore ||
      isPremium;

    return {
      label,
      icon: <MoveToFolderIcon size={IconSize.SMALL} />,
      disabled: isDisabled,
      submenu: !isDisabled && (
        <GallectionListMenu
          // For subfolders, we want to allow moving them one level up
          // and turning them into root-level folders. This is done by moving
          // them to the featuredFolder. Thanks back-end.
          firstItem={
            isSubfolder && featuredFolderId
              ? {
                  label: t('mediapopup.itemMenu.moveUp'),
                  value: featuredFolderId,
                }
              : undefined
          }
          onlyTopLevelFolders
          excludeFolderIds={[
            gallection.folderId,
            gallection.parentId,
            ...(!isSubfolder && featuredFolderId ? [featuredFolderId] : []),
          ]}
          disableFolderIds={gallections
            .filter(g => !!g.premiumFolderData)
            .map(g => g.folderId)}
          disableReasonHint={t(
            'mediapopup.itemMenu.gallery.moveFolderTo.tooltip.disabledDueToPremiumDestination'
          )}
          onSelect={toFolderId =>
            props.moveGallection({
              folderId: gallection.folderId,
              toFolderId,
            })
          }
        />
      ),
    };
  }

  renderPopup() {
    const { gallection, t } = this.props;
    const { popupType } = this.state;
    if (!popupType) {
      return null;
    }
    if (popupType === 'rename') {
      return (
        <PromptModal
          isOpen
          title={t!(
            `mediapopup.gallectionMenu.${gallection.type}.prompt.rename.title`
          )}
          inputLabel={t!('mediapopup.gallections.input.name')}
          onInputValidate={this.onValidateName}
          initialInputValue={gallection.name}
          onCancel={this.closePopup}
          doneButtonLabel={t!('mediapopup.rename')}
          onDone={this.rename}
          maxLength={MAX_TITLE_LENGTH}
          minLength={1}
        />
      );
    } else if (popupType === 'set-cover') {
      return (
        <GallectionCoverSelector
          gallection={gallection}
          onClose={this.closePopup}
        />
      );
    }
  }

  closePopup = () => {
    this.setState({ popupType: null });
  };

  openRemoveConfirmationPopup = () => {
    this.setState({ popupType: 'remove' });
  };

  openRenamePromptPopup = () => {
    this.setState({ popupType: 'rename' });
  };

  openCoverSelectionPopup = () => {
    this.setState({ popupType: 'set-cover' });
  };

  remove = () => {
    const props = this.props;
    props.removeGallection(props.gallection.folderId);
  };

  onValidateName = (name: string) => {
    const { t, existingGallectionNames, gallection } = this.props;
    return getFolderNameValidationErrorMessage(
      name,
      t,
      existingGallectionNames,
      gallection.type === 'gallery'
    );
  };

  rename = name => {
    const {
      gallection: { folderId },
    } = this.props;

    this.props.setGallectionName({
      folderId,
      name,
    });
    this.closePopup();
  };
}

export default enhance(
  GallectionControlButtons
) as any as React.ComponentType<Props>;
